import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface ShortcutsContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const ShortcutsContext = React.createContext<ShortcutsContextValue | undefined>(undefined);

export function useShortcuts(): ShortcutsContextValue {
  const ctx = React.useContext(ShortcutsContext);
  if (!ctx) throw new Error('useShortcuts must be used within KeyboardShortcutsProvider');
  return ctx;
}

function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
}

const NAV: Record<string, string> = {
  d: '/dashboard',
  r: '/repositories',
  i: '/issues',
  p: '/reports',
  s: '/settings',
};

/**
 * Provides the global keyboard layer described in the mockup:
 *   ?            toggle this overlay
 *   g then d/r/i/p/s   navigate
 *   n            new scan (dispatches `debtdrone:new-scan`)
 * Mount inside the Router.
 */
export function KeyboardShortcutsProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const pendingG = React.useRef(false);
  const gTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // ⌘K / Ctrl+K toggles the shortcuts overlay.
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      // `?` toggles the overlay.
      if (e.key === '?') {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }

      // g-then-key navigation.
      if (pendingG.current) {
        const dest = NAV[e.key.toLowerCase()];
        pendingG.current = false;
        if (gTimer.current) clearTimeout(gTimer.current);
        if (dest) {
          e.preventDefault();
          navigate(dest);
          return;
        }
      }
      if (e.key.toLowerCase() === 'g') {
        pendingG.current = true;
        if (gTimer.current) clearTimeout(gTimer.current);
        gTimer.current = setTimeout(() => (pendingG.current = false), 1200);
        return;
      }

      // n = new scan.
      if (e.key.toLowerCase() === 'n') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('debtdrone:new-scan'));
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (gTimer.current) clearTimeout(gTimer.current);
    };
  }, [navigate]);

  const value = React.useMemo(
    () => ({ open, setOpen, toggle: () => setOpen((o) => !o) }),
    [open]
  );

  return (
    <ShortcutsContext.Provider value={value}>
      {children}
      <ShortcutsOverlay open={open} onOpenChange={setOpen} />
    </ShortcutsContext.Provider>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block min-w-[20px] rounded border border-white/10 border-b-2 bg-accent px-1.5 py-px text-center font-mono text-[10px] text-foreground">
      {children}
    </span>
  );
}

function Row({ desc, keys }: { desc: string; keys: string[] }) {
  return (
    <div className="flex items-center justify-between border-b border-white/[0.04] py-1.5 last:border-b-0">
      <span className="text-xs text-muted-foreground">{desc}</span>
      <span className="flex gap-1">
        {keys.map((k, i) => (
          <Key key={i}>{k}</Key>
        ))}
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function ShortcutsOverlay({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('w-[440px] max-w-[calc(100vw-2rem)] border-hairline bg-card p-6')}>
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Keyboard Shortcuts</DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          <Section title="Navigation">
            <Row desc="Go to Dashboard" keys={['G', 'D']} />
            <Row desc="Go to Repositories" keys={['G', 'R']} />
            <Row desc="Go to Issues" keys={['G', 'I']} />
            <Row desc="Go to Reports" keys={['G', 'P']} />
            <Row desc="Settings" keys={['G', 'S']} />
          </Section>
          <Section title="Actions">
            <Row desc="New scan" keys={['N']} />
          </Section>
          <Section title="This overlay">
            <Row desc="Show / hide shortcuts" keys={['?']} />
            <Row desc="Show / hide shortcuts" keys={['⌘', 'K']} />
            <Row desc="Dismiss" keys={['Esc']} />
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default KeyboardShortcutsProvider;
